import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Link, Route, Routes } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, QrCode, RotateCcw, Share2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { BusinessCard } from "@/components/BusinessCard";
import { ContactActions } from "@/components/ContactActions";
import { EmailSignature } from "@/components/EmailSignature";
import { Marquee } from "@/components/Marquee";
import { QrModal } from "@/components/QrModal";
import { FadeUp, Reveal } from "@/components/Reveal";
import { PrintKit } from "@/components/PrintKit";
import { SaveContact } from "@/components/SaveContact";
import { LOGO_URL, PAYOFF, QUOTE, ROLE, SITE_URL } from "@/lib/contact";
import { downloadVCard } from "@/lib/vcard";
import { shareCard } from "@/lib/share";

const Chapter = ({ number, title }) => (
    <div className="mb-8 flex items-center gap-4">
        <span className="font-display text-3xl italic text-[#e2c075]">{number}</span>
        <span className="gold-hairline flex-1" />
        <span className="font-code text-[10px] uppercase tracking-[0.3em] text-[#a1a8b3]">{title}</span>
    </div>
);

const CardPage = () => {
    const [flipped, setFlipped] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const heroRef = useRef(null);

    const handleHeroVCard = () => {
        downloadVCard();
        toast.success("Contatto scaricato", {
            description: "Apri il file .vcf per salvare Mino in rubrica.",
        });
    };
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const titleY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const glowY = useTransform(scrollYProgress, [0, 1], [0, -90]);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    return (
        <div className="relative min-h-screen bg-[#090a0c]" data-testid="digital-business-card">
            <div className="grain" aria-hidden="true" />
            <Toaster theme="dark" position="bottom-center" />

            <header className="glass fixed inset-x-0 top-0 z-50 border-b border-[rgba(226,192,117,0.14)]">
                <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-4">
                    <span className="flex items-baseline gap-3">
                        <span className="font-display text-xl font-semibold text-[#e2c075]">MC</span>
                        <span className="font-code text-[10px] uppercase tracking-[0.3em] text-[#a1a8b3]">
                            Mino Cesari
                        </span>
                    </span>
                    <a
                        data-testid="header-site-link"
                        href={SITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-code text-[10px] uppercase tracking-[0.25em] text-[#e2c075] transition-opacity hover:opacity-70"
                    >
                        minocesari.com
                    </a>
                </div>
            </header>

            <main className="mx-auto max-w-2xl px-5">
                <section ref={heroRef} className="relative flex min-h-[92vh] flex-col justify-center overflow-hidden pt-24" data-testid="hero-section">
                    <motion.div
                        style={{ y: glowY }}
                        className="gold-glow pointer-events-none absolute -top-24 left-1/2 h-[480px] w-[480px] -translate-x-1/2"
                        aria-hidden="true"
                    />
                    <motion.div style={{ y: titleY }} className="relative">
                        <Reveal delay={0.15}>
                            <span className="font-code text-[10px] uppercase tracking-[0.4em] text-[#686f7c]">
                                Biglietto da visita digitale — N° 01
                            </span>
                        </Reveal>
                        <h1 className="font-display mt-6 text-5xl font-semibold leading-[0.95] tracking-tight text-[#f4f1ea] sm:text-6xl lg:text-7xl" data-testid="hero-title">
                            <Reveal delay={0.3}>Mino</Reveal>
                            <Reveal delay={0.42}>
                                <span className="italic text-gold-gradient">Cesari</span>
                            </Reveal>
                        </h1>
                        <Reveal delay={0.6} className="mt-7">
                            <span className="font-code text-[11px] uppercase tracking-[0.28em] text-[#e2c075]">
                                {ROLE}
                            </span>
                        </Reveal>
                        <Reveal delay={0.75} className="mt-6">
                            <span className="font-display block max-w-md text-lg italic leading-snug text-[#a1a8b3]">
                                “{QUOTE}”
                            </span>
                        </Reveal>
                        <Reveal delay={0.9} className="mt-9">
                            <span className="flex flex-wrap items-center gap-3">
                                <button
                                    data-testid="hero-vcard-button"
                                    onClick={handleHeroVCard}
                                    className="group relative flex items-center gap-2.5 overflow-hidden rounded-full bg-[#e2c075] px-6 py-3.5 transition-transform duration-300 active:scale-[0.97]"
                                >
                                    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                                    <UserPlus className="h-4 w-4 text-[#090a0c]" />
                                    <span className="font-code text-[11px] font-medium uppercase tracking-[0.2em] text-[#090a0c]">
                                        Salva il contatto
                                    </span>
                                </button>
                                <button
                                    data-testid="hero-share-button"
                                    onClick={shareCard}
                                    className="flex items-center gap-2.5 rounded-full border border-[rgba(226,192,117,0.3)] px-6 py-3.5 text-[#e2c075] transition-colors duration-300 hover:bg-[rgba(226,192,117,0.06)]"
                                >
                                    <Share2 className="h-4 w-4" />
                                    <span className="font-code text-[11px] uppercase tracking-[0.2em]">Condividi</span>
                                </button>
                                <button
                                    data-testid="hero-qr-button"
                                    onClick={() => setQrOpen(true)}
                                    className="flex items-center gap-2.5 rounded-full border border-[rgba(226,192,117,0.3)] px-6 py-3.5 text-[#e2c075] transition-colors duration-300 hover:bg-[rgba(226,192,117,0.06)]"
                                >
                                    <QrCode className="h-4 w-4" />
                                    <span className="font-code text-[11px] uppercase tracking-[0.2em]">Mostra il QR</span>
                                </button>
                            </span>
                        </Reveal>
                    </motion.div>
                    <motion.div
                        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-3"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.4, duration: 0.8 }}
                    >
                        <span className="font-code text-[9px] uppercase tracking-[0.35em] text-[#686f7c]">Scorri</span>
                        <span className="scroll-line block h-10 w-px bg-[#e2c075]/60" />
                        <ChevronDown className="h-3.5 w-3.5 text-[#e2c075]/70" />
                    </motion.div>
                </section>
            </main>

            <Marquee />

            <main className="mx-auto max-w-2xl px-5">
                <section className="py-20" data-testid="card-section">
                    <FadeUp>
                        <Chapter number="01" title="La carta" />
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <BusinessCard flipped={flipped} />
                    </FadeUp>
                    <FadeUp delay={0.2} className="mt-8 flex flex-col gap-3 sm:flex-row">
                        <button
                            data-testid="card-flip-button"
                            onClick={() => setFlipped((f) => !f)}
                            className="flex flex-1 items-center justify-center gap-3 rounded-xl border border-[rgba(226,192,117,0.3)] px-6 py-4 text-[#e2c075] transition-colors duration-300 hover:bg-[rgba(226,192,117,0.06)]"
                        >
                            <RotateCcw className="h-4 w-4" />
                            <span className="font-code text-xs uppercase tracking-[0.22em]">
                                {flipped ? "Vedi il fronte" : "Gira la carta"}
                            </span>
                        </button>
                        <button
                            data-testid="qr-code-button"
                            onClick={() => setQrOpen(true)}
                            className="flex flex-1 items-center justify-center gap-3 rounded-xl border border-[rgba(226,192,117,0.3)] px-6 py-4 text-[#e2c075] transition-colors duration-300 hover:bg-[rgba(226,192,117,0.06)]"
                        >
                            <QrCode className="h-4 w-4" />
                            <span className="font-code text-xs uppercase tracking-[0.22em]">QR a tutto schermo</span>
                        </button>
                    </FadeUp>
                </section>

                <section className="pb-20" data-testid="contacts-section">
                    <FadeUp>
                        <Chapter number="02" title="Contatti" />
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <ContactActions />
                    </FadeUp>
                </section>

                <section className="pb-24" data-testid="save-section">
                    <FadeUp>
                        <Chapter number="03" title="Restiamo in contatto" />
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <SaveContact />
                    </FadeUp>
                </section>
            </main>

            <footer className="border-t border-[rgba(226,192,117,0.14)] py-14">
                <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 px-5">
                    <img src={LOGO_URL} alt="MC — Mino Cesari" className="h-12 opacity-90" data-testid="footer-logo" />
                    <p className="font-display text-center text-base italic text-[#a1a8b3]">“{QUOTE}”</p>
                    <div className="gold-hairline w-40" />
                    <p className="font-code text-[9px] uppercase tracking-[0.3em] text-[#686f7c]">
                        © MMXXVI Mino Cesari — {PAYOFF}
                    </p>
                </div>
            </footer>

            <QrModal open={qrOpen} onClose={() => setQrOpen(false)} />
        </div>
    );
};

const StudioPage = () => (
    <div className="relative min-h-screen bg-[#090a0c]" data-testid="studio-page">
        <Toaster theme="dark" position="bottom-center" />
        <header className="glass fixed inset-x-0 top-0 z-50 border-b border-[rgba(226,192,117,0.14)]">
            <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-4">
                <span className="flex items-baseline gap-3">
                    <span className="font-display text-xl font-semibold text-[#e2c075]">MC</span>
                    <span className="font-code text-[10px] uppercase tracking-[0.3em] text-[#a1a8b3]">
                        Area personale
                    </span>
                </span>
                <Link
                    data-testid="studio-back-link"
                    to="/"
                    className="font-code text-[10px] uppercase tracking-[0.25em] text-[#e2c075] transition-opacity hover:opacity-70"
                >
                    ← Torna al biglietto
                </Link>
            </div>
        </header>
        <main className="mx-auto max-w-2xl px-5 pt-28">
            <section className="pb-20" data-testid="print-section">
                <FadeUp>
                    <Chapter number="01" title="Per la tipografia" />
                </FadeUp>
                <FadeUp delay={0.1}>
                    <PrintKit />
                </FadeUp>
            </section>
            <section className="pb-24" data-testid="signature-section">
                <FadeUp>
                    <Chapter number="02" title="Firma email" />
                </FadeUp>
                <FadeUp delay={0.1}>
                    <EmailSignature />
                </FadeUp>
            </section>
        </main>
        <footer className="border-t border-[rgba(226,192,117,0.14)] py-10">
            <p className="font-code text-center text-[9px] uppercase tracking-[0.3em] text-[#686f7c]">
                Pagina riservata — non condividere questo link
            </p>
        </footer>
    </div>
);

function App() {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<CardPage />} />
                <Route path="/studio" element={<StudioPage />} />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
